import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Backdrop,
  Button,
  Dialog,
  Grid,
  LinearProgress,
  Paper,
  TextField,
} from "@material-ui/core";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import useAuth from "../../../../hooks/auth";
import { Alert } from "../../../ui";
import "./calendar.scss";

const TaskDetails = ({ taskEvent, tasks, closeModal }) => {
  const { user } = useAuth();

  const task = tasks.filter((t) => {
    return t.id == taskEvent.event.id;
  })[0];

  const [note, setNote] = useState(task.note || "");

  const getStatusStr = () => {
    switch (task.status) {
      case "late":
        return "Atrasada";
      case "done":
        return "Concluída";
      case "cancelled":
        return "Cancelada";
      default:
        return "Em andamento";
    }
  };

  const dateToString = (date) => {
    const d = new Date(date);
    return d.toLocaleString("pt-br");
  };

  const updateTask = async (event) => {
    try {
      await axios.put(`/v1/crm/trades/${task.crm_trade_id}/tasks/${task.id}`, {
        button: event.target.innerText,
        note: note,
      });
    } catch {
      alert(
        "Aconteceu um problema ao tentar atualizar essa tarefa. Favor, confira mais tarde e tente novamente!"
      );
    } finally {
      closeModal();
    }
  };

  return (
    <Grid container spacing={3} className="tasks-modal">
      <div>
        <Grid item sm={12}>
          <div className="task-header">
            Tarefa: <span className="task-value">{taskEvent.event.title}</span>
            <span> ({getStatusStr()})</span>
          </div>

          {user.type != "operational" && (
            <div className="task-header">
              Responsável <span className="task-value">{task.assignee_by}</span>
            </div>
          )}

          <div className="task-header">
            Criada por{" "}
            <span className="task-value">{task["assigned-by"] || "você"}</span>
          </div>

          {task.description && (
            <>
              <hr />
              <span className="task-header">Descrição: </span>
              <span className="task-value">{task.description}</span>
            </>
          )}

          <hr />

          <div className="task-header">
            Início em{" "}
            <span className="task-value">{dateToString(task.start)}</span>
          </div>

          <div className="task-header">
            Prazo final{" "}
            <span className="task-value">
              {dateToString(moment(task.end).subtract(1, "days").toISOString())}
            </span>
          </div>
        </Grid>

        <Grid item sm={12}>
          <TextField
            label="Observações"
            disabled={task.status == "done" || task.status == "cancelled"}
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Grid>

        <Grid item sm={12}>
          <Button
            fullWidth
            disabled={task.status == "done" || task.status == "cancelled"}
            style={{ marginTop: "10px" }}
            variant="outlined"
            color="primary"
            onClick={updateTask}
          >
            Finalizar tarefa
          </Button>

          <Button
            fullWidth
            disabled={task.status == "done" || task.status == "cancelled"}
            style={{ marginTop: "10px" }}
            variant="outlined"
            color="secondary"
            onClick={updateTask}
          >
            Cancelar tarefa
          </Button>
        </Grid>
      </div>

      <button
        title="Fechar tarefa"
        type="button"
        tabIndex="0"
        className="mm-ico-close custom-close-btn-modal"
        onClick={closeModal}
        id="close-start-form"
      >
        X
      </button>
    </Grid>
  );
};

export default function CrmCalendar() {
  const [state, setState] = useState({
    tasks: [],
    show: false,
    task: {},
    loading: false,
    error: null,
  });

  const closeModal = () => setState({ ...state, show: false });

  const viewTask = (e) => {
    e.preventDefault();
    setState({ ...state, show: true, task: e });
  };

  useEffect(() => {
    setState({ tasks: [], task: {}, show: false, loading: true, error: null });

    axios
      .request({
        method: "get",
        url: `/v1/crm/trades/tasks`,
      })
      .then((response) => {
        const { data } = response;
        let tasks = [];

        if (data != null) {
          data.map((task) => {
            task["allDay"] = true;
            tasks.push(task);
          });
        }

        setState({
          ...state,
          task: {},
          tasks: tasks,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        setState({
          ...state,
          task: {},
          loading: false,
          tasks: [],
          error: error,
        });
      });
  }, []);

  return (
    <div className="calendar-flex">
      <div>
        {state.loading && <LinearProgress style={{ marginBottom: "20px" }} />}

        {state.error && (
          <div style={{ marginBottom: "20px" }}>
            <Alert
              title="ATENÇÃO!"
              message="Não conseguimos retornar suas tarefas no momento. Tente novamente mais tarde ou entre em contato conosco se o problema persistir."
              severity="error"
            />
          </div>
        )}

        {!state.loading && state.tasks.length == 0 && (
          <div style={{ marginBottom: "20px" }}>
            <Alert
              message="Não existem tarefas para você no momento."
              severity="info"
            />
          </div>
        )}

        <Paper elevation={4}>
          <div className="tasks-calendar">
            <FullCalendar
              eventDataTransform={(event) => {
                if (event.allDay) {
                  event.end = moment(event.end).add(1, "days").toISOString();
                }

                return event;
              }}
              windowResize={100}
              contentHeight={500}
              events={state.tasks}
              locale="pt-BR"
              allDayText="Dia inteiro"
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              eventClick={viewTask}
              selectable={true}
              hiddenDays={[]}
              buttonText={{
                today: "Ir para data atual",
                month: "Mês",
                week: "Semana",
                day: "Dia",
                list: "Lista",
              }}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: null,
              }}
            />

            <Dialog
              aria-describedby="modal para visualizar tarefa"
              aria-labelledby="visualizar-tarefa"
              style={{ maxHeight: "75%", margin: "auto" }}
              maxWidth={false}
              open={state.show}
              onClose={closeModal}
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{ timeout: 500 }}
            >
              {
                <TaskDetails
                  taskEvent={state.task}
                  closeModal={closeModal}
                  tasks={state.tasks}
                />
              }
            </Dialog>
          </div>
        </Paper>
      </div>

      <div className="calendar-bottom"></div>
    </div>
  );
}
